<script setup lang="ts">
    import { computed, toRef } from 'vue'

    import type {
        AlternativeAttributeValueChangeReadModel,
        AttributeReadModel
    } from '@Evaluation/ValueAnalysis/Domain/readmodels'

    import type { SaveToAttributeDialogViewModel } from '@Evaluation/ValueAnalysis/Components/SaveToAttributeDialog'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: SaveToAttributeDialogViewModel
    }>()

    const emit = defineEmits<{
        (e: 'save-existing-attribute', attributeId: number, values: string[]): void
        (e: 'save-new-attribute', attributeName: string, values: string[]): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- computed ----------------

    const opened = computed(() => vm.value?.opened ?? false)

    const saving = computed(() => vm.value.saving)

    const allAttributes = computed<AttributeReadModel[]>(() => vm.value.attributes)

    const attributes = computed<AttributeReadModel[]>(() =>
        allAttributes.value.filter(a =>
            a.IsNumeric === true &&
            a.IsManual === true &&
            a.IsMultivalue === false
        )
    )

    const duplicatedName = computed<boolean>(() =>
        allAttributes.value.some(a =>
            a.Name.toLowerCase() === vm.value.newAttributeName?.toLowerCase()
        )
    )

    const attributeName = computed<string>(() =>
        useExistingAttribute.value === true
            ? existingAttribute.value?.Name ?? ''
            : newAttributeName.value
    )

    const changes = computed<AlternativeAttributeValueChangeReadModel[]>(() =>
        vm.value.changes
    )

    // ---------------- writable computed (VM-backed) ----------------

    const step = computed<string>({
        get: () => vm.value.step,
        set: val => { vm.value.step = val }
    })

    const newAttributeName = computed<string>({
        get: () => vm.value.newAttributeName,
        set: val => { vm.value.newAttributeName = val }
    })

    const existingAttribute = computed<AttributeReadModel | null>({
        get: () => vm.value.existingAttribute,
        set: val => { vm.value.existingAttribute = val }
    })

    const useExistingAttribute = computed<boolean>({
        get: () => vm.value.useExistingAttribute,
        set: val => { vm.value.useExistingAttribute = val }
    })

    // ---------------- actions ----------------

    function saveChanges()
    {
        const values = changes.value
            ?.filter(a => a.NewValue != null)
            .map(a => `${a.Id}:${a.NewValue}`) ?? []

        if (useExistingAttribute.value === true)
        {
            if (!existingAttribute.value) return

            emit(
                'save-existing-attribute',
                existingAttribute.value.Id,
                values
            )
        }
        else
        {
            emit(
                'save-new-attribute',
                newAttributeName.value,
                values
            )
        }
    }

    function showStart()
    {
        step.value = 'start'
        vm.value.changes = null
    }

    function showPreview()
    {
        if (useExistingAttribute.value === true && existingAttribute.value == null)
        {
            return
        }

        const attrId = useExistingAttribute.value === true
            ? existingAttribute.value!.Id
            : null

        step.value = 'preview'
        vm.value.showChanges(attrId)
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div v-if="step === 'start'" class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Save to attribute</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <div class="card-deck">
                            <div class="card" :class="{'border-primary' : useExistingAttribute === false }">
                                <div class="card-header pointer" @click="useExistingAttribute = false" :class="{'text-white bg-primary' : useExistingAttribute === false }">
                                    <i class="fa-regular" aria-hidden="true" :class="{'fa-square': useExistingAttribute === true, 'fa-square-check': useExistingAttribute === false}"></i>
                                    Create new attribute
                                </div>
                                <div class="card-body">
                                    <div class="form-group">
                                        <input type="text" class="form-control" v-model.trim="newAttributeName">
                                        <small v-if="duplicatedName === true" class="text-danger">Sorry, this name is already being used. Please change the name to a unique name.</small>
                                        <small class="form-text text-muted">Values will be saved as a new attribute in "Value scores" section.</small>
                                    </div>
                                </div>
                            </div>
                            <div class="card" :class="{'border-primary' : useExistingAttribute === true}">
                                <div class="card-header pointer" @click="useExistingAttribute = true" :class="{'text-white bg-primary' : useExistingAttribute === true }">
                                    <i class="fa-regular" aria-hidden="true" :class="{'fa-square': useExistingAttribute === false, 'fa-square-check': useExistingAttribute === true}"></i>
                                    Use existing attribute
                                </div>
                                <div class="card-body">
                                    <div class="form-group">
                                        <select v-model="existingAttribute" class="form-control">
                                            <option :value="null">Choose...</option>
                                            <option v-for="attr in attributes" :value="attr">{{attr.Name}}</option>

                                        </select>
                                        <small class="form-text text-muted">
                                            Values will be saved to the existing attribute.
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button @click="showPreview" type="button" class="btn btn-primary">Preview</button>
                        <button type="button" class="btn btn-outline-secondary" @click="close">Cancel</button>
                    </div>
                </div>
                <div v-if="step === 'preview'" class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Preview</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <table class="table table-bordered table-hover">
                            <thead>
                                <tr>
                                    <th>{{attributeName}}</th>
                                    <th>Value</th>
                                </tr>
                            </thead>
                            <tbody v-if="changes">
                                <tr v-for="a in changes" :key="a.Id">
                                    <td>{{a.Name}}</td>
                                    <td class="text-center">
                                        <span>{{a.NewValue}}</span>
                                        <small v-if="a.OldValue != null">
                                            <br />
                                            (Current: <span class="text-danger">{{a.OldValue}}</span>)
                                        </small>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                    </div>

                    <div class="modal-footer d-flex justify-content-between">
                        <button type="button" class="btn btn-outline-secondary" @click="showStart">Back</button>
                        <div>
                            <button v-if="saving === true" type="button" class="btn btn-primary" @click="saveChanges">
                                <i class="fa fa-spinner fa-spin"></i> Saving...
                            </button>
                            <button v-if="saving === false" type="button" class="btn btn-primary" @click="saveChanges">Save</button>
                            <button type="button" class="btn btn-outline-secondary" @click="close">Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>